import jsPDF from 'jspdf';
import React, { useEffect, useState, useRef } from 'react';
import { saveProductsOffline, getProductsOffline } from '../utils/offlineDb';
import API from '../api';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import JsBarcode from 'jsbarcode';
import { priceToCode, getPriceCodeSettings, isBarcodeOnly } from '../utils/priceCode';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const [buyingPrice, setBuyingPrice] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [stock, setStock] = useState('');
  const [lowStockThreshold, setLowStockThreshold] = useState('5');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editName, setEditName] = useState('');
  const [editBuyingPrice, setEditBuyingPrice] = useState('');
  const [editSellingPrice, setEditSellingPrice] = useState('');
  const [editStock, setEditStock] = useState('');
  const [editLowStockThreshold, setEditLowStockThreshold] = useState('5');
  const [barcodeProduct, setBarcodeProduct] = useState(null);
  const barcodeRef = useRef(null);
  const [printQuantity, setPrintQuantity] = useState(1);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
const [labelSize, setLabelSize] = useState(JSON.parse(localStorage.getItem('shopProfile') || '{}').barcode_label_size || '50x25');
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await API.get('/products');
      setProducts(res.data);
      saveProductsOffline(res.data);
    } catch (err) {
      console.error(err);
      const offlineProducts = await getProductsOffline();
      if (offlineProducts.length > 0) {
        setProducts(offlineProducts);
        toast.error('No internet — showing saved offline data');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (barcodeProduct && barcodeRef.current) {
      JsBarcode(barcodeRef.current, barcodeProduct.barcode, {
        format: 'CODE128',
        width: 2,
        height: 50,
        displayValue: true,
        fontSize: 12,
      });
    }
  }, [barcodeProduct]);

  useEffect(() => {
    if (barcodeProduct) {
      const [w] = labelSize.split('x').map(Number);
      const scale = w / 50;
      setBarcodeImageUrl(generateBarcodeImage(barcodeProduct.barcode, scale));
    }
  }, [barcodeProduct, labelSize]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (parseFloat(sellingPrice) < parseFloat(buyingPrice)) {
      toast.error('Selling price cannot be less than buying price!');
      return;
    }
    setAdding(true);
    try {
      await API.post('/products', {
        name,
        buying_price: parseFloat(buyingPrice),
        selling_price: parseFloat(sellingPrice),
        stock: parseInt(stock),
        low_stock_threshold: parseInt(lowStockThreshold) || 5,
      });
      toast.success('Product added!');
      setName('');
      setBuyingPrice('');
      setSellingPrice('');
      setStock('');
      setLowStockThreshold('5');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add product');
    }
    setAdding(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await API.delete(`/products/${id}`);
      toast.success('Product deleted!');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete product');
    }
  };

  const handleEditClick = (p) => {
    setEditProduct(p);
    setEditName(p.name);
    setEditBuyingPrice(p.buying_price);
    setEditSellingPrice(p.selling_price);
    setEditStock(p.stock);
    setEditLowStockThreshold(p.low_stock_threshold || 5);
  };

  const handleEditSave = async () => {
    if (parseFloat(editSellingPrice) < parseFloat(editBuyingPrice)) {
      toast.error('Selling price cannot be less than buying price!');
      return;
    }
    try {
      await API.put(`/products/${editProduct.id}`, {
        name: editName,
        buying_price: parseFloat(editBuyingPrice),
        selling_price: parseFloat(editSellingPrice),
        stock: parseInt(editStock),
        low_stock_threshold: parseInt(editLowStockThreshold) || 5,
      });
      toast.success('Product updated!');
      setEditProduct(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update product');
    }
  };

  const [barcodeImageUrl, setBarcodeImageUrl] = useState('');

  const generateBarcodeImage = (barcodeValue, scale) => {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, barcodeValue, {
      format: 'CODE128',
      width: Math.max(0.6, 1.5 * scale) * 3,
      height: Math.max(15, 30 * scale) * 3,
      displayValue: true,
      fontSize: Math.max(5, 8 * scale) * 3,
      margin: 0,
    });
    return canvas.toDataURL('image/png');
  };

const [labelsPerRow, setLabelsPerRow] = useState(String(JSON.parse(localStorage.getItem('shopProfile') || '{}').barcode_labels_per_row || 1));
  const handlePrintBarcode = (p) => {
    setBarcodeProduct(p);
    setPrintQuantity(1);
  };

  const handleDownloadPdf = () => {
    const [w, h] = labelSize.split('x').map(Number);
    const upCount = parseInt(labelsPerRow) || 1;
    const totalQty = printQuantity;
    const rows = Math.ceil(totalQty / upCount);
    const pageWidth = w * upCount;
    const pageHeight = h * rows;
    const scale = w / 50;

    const orientation = pageWidth >= pageHeight ? 'landscape' : 'portrait';
    const doc = new jsPDF({
      orientation,
      unit: 'mm',
      format: [pageWidth, pageHeight],
    });
    const barcodeImg = generateBarcodeImage(barcodeProduct.barcode, scale);

    for (let i = 0; i < totalQty; i++) {
      const col = i % upCount;
      const row = Math.floor(i / upCount);
      const x = col * w;
      const y = row * h;

      doc.setFontSize(Math.max(6, 7 * scale));
      doc.setFont(undefined, 'bold');
      doc.text(barcodeProduct.name, x + w / 2, y + 3, { align: 'center', maxWidth: w - 2 });

      const barcodeWidth = w - 4;
      const barcodeHeight = h * 0.45;
      doc.addImage(barcodeImg, 'PNG', x + 2, y + 4.5, barcodeWidth, barcodeHeight);

      doc.setFontSize(Math.max(7, 9 * scale));
      doc.text(`LKR ${parseFloat(barcodeProduct.selling_price).toFixed(2)}`, x + w / 2, y + h - 2, { align: 'center' });
    }

    doc.save(`barcode-${barcodeProduct.barcode}-${totalQty}stickers.pdf`);
  };

  const handlePrint = () => {
    const printDiv = document.getElementById('barcode-print');
    if (printDiv) printDiv.style.display = 'block';
    setTimeout(() => {
      window.print();
      if (printDiv) printDiv.style.display = 'none';
    }, 200);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await API.post('/products/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    }
    setImporting(false);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const template = `Product Name,Buying Price,Selling Price,Stock\nShirt,1500,2000,50\nShoe,2500,3500,30\nRice 5kg,800,950,100`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wahi-pos-product-template.csv';
    a.click();
  };

  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.barcode && p.barcode.includes(search))
  );

  return (
    <div>
      <Navbar />
      <style>{`
        .edit-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5); display: flex;
          align-items: center; justify-content: center; z-index: 1000;
        }
        .edit-modal {
          background: #fff; border-radius: 12px; padding: 2rem;
          width: 100%; max-width: 400px; box-shadow: 0 4px 24px rgba(0,0,0,0.15);
        }
        @media print {
          body * { visibility: hidden; }
          #barcode-print, #barcode-print * { visibility: visible; }
          #barcode-print { 
            position: absolute; left: 0; top: 0; width: 100%;
            display: block !important;
          }
          #barcode-print > div {
            page-break-after: always;
            break-after: page;
            width: 100%;
            height: 100%;
          }
          #barcode-print > div:last-child {
            page-break-after: auto;
          }
          .no-print { display: none; }
          @page { 
            margin: 0; 
            size: ${labelSize === '50x25' ? '50mm 25mm' : labelSize === '40x30' ? '40mm 30mm' : '30mm 20mm'}; 
          }
        }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      {barcodeProduct && (
        <div className="edit-overlay" onClick={() => setBarcodeProduct(null)}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem', fontSize: '16px' }}>🔳 Print Barcode Stickers</h3>
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '1rem' }}>
              Using your saved settings: {labelSize.replace('x', 'mm x ')}mm, {labelsPerRow}-up roll.
              <a href="/profile" style={{ color: '#4f46e5', marginLeft: '4px' }}>Change in Settings</a>
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '13px', color: '#888', display: 'block', marginBottom: '4px' }}>
                How many stickers to print?
              </label>
              <input
                style={{ ...styles.modalInput, width: '100px' }}
                type="number"
                min="1"
                max="1000"
                value={printQuantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setPrintQuantity('');
                  } else {
                    const num = parseInt(val);
                    if (num >= 1) setPrintQuantity(num);
                  }
                }}
              />
            </div>

            <div id="barcode-print" style={{ display: 'none' }}>
              {Array.from({ length: printQuantity }).map((_, i) => {
                const [w, h] = labelSize.split('x').map(Number);
                const scale = w / 50;
                const nameFontSize = Math.max(4, Math.round(7 * scale));
                const priceFontSize = Math.max(5, Math.round(9 * scale));
                const codeFontSize = Math.max(4, Math.round(8 * scale));
                const barcodeHeight = Math.max(12, Math.round(30 * scale));
                return (
                <div key={i} style={{
                  width: `${w}mm`,
                  height: `${h}mm`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5mm',
                  boxSizing: 'border-box',
                  pageBreakInside: 'avoid',
                  overflow: 'hidden',
                  border: '0.5px solid #eee',
                  marginBottom: '1mm',
                }}>
                  <p style={{ 
                    fontSize: `${nameFontSize}pt`, 
                    fontWeight: 'bold', 
                    margin: '0', 
                    textAlign: 'center', 
                    width: `${w - 1}mm`, 
                    wordBreak: 'break-word', 
                    whiteSpace: 'normal', 
                    lineHeight: '1.1', 
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {barcodeProduct.name}
                  </p>
<img src={barcodeImageUrl} alt="barcode" style={{ maxWidth: `${w - 1}mm`, width: '100%' }} />                  <p style={{ fontSize: `${priceFontSize}pt`, fontWeight: 'bold', margin: '0' }}>
                    LKR {parseFloat(barcodeProduct.selling_price).toFixed(2)}
                  </p>
                  {getPriceCodeSettings().enabled && (
                    <p style={{ fontSize: `${codeFontSize}pt`, color: '#555', margin: '0', letterSpacing: '0.5px' }}>
                      {priceToCode(barcodeProduct.buying_price)}
                    </p>
                  )}
                </div>
              );})}
            </div>

            <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
              <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 4px' }}>{barcodeProduct.name}</p>
              <svg ref={barcodeRef}></svg>
              <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '4px 0 0', color: '#4f46e5' }}>
                LKR {parseFloat(barcodeProduct.selling_price).toFixed(2)}
              </p>
              {getPriceCodeSettings().enabled && (
                <p style={{ fontSize: '13px', color: '#7c3aed', letterSpacing: '2px', margin: '2px 0 0' }}>
                  {priceToCode(barcodeProduct.buying_price)}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }} className="no-print">
              <button
                onClick={handleDownloadPdf}
                style={{ ...styles.saveBtn, background: '#16a34a', opacity: !printQuantity || printQuantity < 1 ? 0.5 : 1 }}
                disabled={!printQuantity || printQuantity < 1}
              >
                📄 Download PDF ({printQuantity || 0} sticker{printQuantity > 1 ? 's' : ''})
              </button>
              <button onClick={() => setBarcodeProduct(null)} style={styles.cancelBtn}>Cancel</button>            
              </div>
          </div>
        </div>
      )}

      {editProduct && (
        <div className="edit-overlay" onClick={() => setEditProduct(null)}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem', fontSize: '18px' }}>Edit Product</h3>
            <input style={styles.modalInput} type="text" placeholder="Product Name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            <input style={styles.modalInput} type="number" placeholder="Buying Price" value={editBuyingPrice} onChange={(e) => setEditBuyingPrice(e.target.value)} min="0" />
            <input style={styles.modalInput} type="number" placeholder="Selling Price" value={editSellingPrice} onChange={(e) => setEditSellingPrice(e.target.value)} min="0" />
            <input style={styles.modalInput} type="number" placeholder="Stock" value={editStock} onChange={(e) => setEditStock(e.target.value)} min="0" />
            <input style={styles.modalInput} type="number" placeholder="Low Stock Alert (e.g. 5)" value={editLowStockThreshold} onChange={(e) => setEditLowStockThreshold(e.target.value)} min="1" />
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button onClick={handleEditSave} style={styles.saveBtn}>Save Changes</button>
              <button onClick={() => setEditProduct(null)} style={styles.cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.container} className="container">
        <div style={styles.header}>
          <h2 style={styles.title}>Products</h2>
        </div>

        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '16px', margin: 0 }}>Add New Product</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={downloadTemplate} style={styles.templateBtn}>
                📥 Download Template
              </button>
              <label style={styles.importBtn}>
                {importing ? '⏳ Importing...' : '📤 Import Excel/CSV'}
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImport}
                  style={{ display: 'none' }}
                  disabled={importing}
                />
              </label>
            </div>
          </div>

          <form onSubmit={handleAdd} style={styles.form} className="form">
            <input style={styles.input} type="text" placeholder="Product Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <input style={styles.input} type="number" placeholder="Buying Price (LKR)" value={buyingPrice} onChange={(e) => setBuyingPrice(e.target.value)} min="0" required />
            <input style={styles.input} type="number" placeholder="Selling Price (LKR)" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} min="0" required />
            <input style={styles.input} type="number" placeholder="Stock" value={stock} onChange={(e) => setStock(e.target.value)} min="1" required />
            <input style={styles.input} type="number" placeholder="Low Stock Alert (e.g. 5)" value={lowStockThreshold} onChange={(e) => setLowStockThreshold(e.target.value)} min="1" />
            <button style={styles.button} type="submit" disabled={adding}>
              {adding ? 'Adding...' : 'Add Product'}
            </button>
          </form>

          {importResult && (
            <div style={styles.importResult}>
              <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                ✅ {importResult.imported} imported | ⚠️ {importResult.skipped} skipped
              </p>
              {importResult.errors?.length > 0 && (
                <div>
                  <p style={{ fontSize: '12px', color: '#ef4444', marginBottom: '0.25rem' }}>Errors:</p>
                  {importResult.errors.map((e, i) => (
                    <p key={i} style={{ fontSize: '12px', color: '#ef4444' }}>• {e}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <input
          style={styles.search}
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (
          <div style={styles.loaderWrapper}>
            <div style={styles.spinner}></div>
            <p style={styles.loaderText}>Loading products...</p>
          </div>
        ) : (
          <div className="mobile-scroll-wrapper"><p className="mobile-scroll-hint">← Swipe to see more →</p><table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Buying (LKR)</th>
                <th style={styles.th}>Selling (LKR)</th>
                <th style={styles.th}>Profit/Unit</th>
                <th style={styles.th}>Stock</th>
                <th style={styles.th}>Barcode</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ ...styles.td, textAlign: 'center', color: '#888' }}>
                    No products found
                  </td>
                </tr>
              ) : (
                filteredProducts.map((p) => (
                  <tr key={p.id}>
                    <td style={styles.td}>{p.name}</td>
                    <td style={styles.td}>
                      {getPriceCodeSettings().enabled && !isBarcodeOnly()
                        ? <span style={styles.codeText}>{priceToCode(p.buying_price)}</span>
                        : parseFloat(p.buying_price).toFixed(2)
                      }
                    </td>
                    <td style={styles.td}>{parseFloat(p.selling_price).toFixed(2)}</td>
                    <td style={{ ...styles.td, color: '#16a34a', fontWeight: 'bold' }}>
                      {getPriceCodeSettings().enabled && !isBarcodeOnly()
                        ? <span style={styles.codeText}>{priceToCode(parseFloat(p.selling_price) - parseFloat(p.buying_price))}</span>
                        : (parseFloat(p.selling_price) - parseFloat(p.buying_price)).toFixed(2)
                      }
                    </td>
                    <td style={{ ...styles.td, color: p.stock < (p.low_stock_threshold || 5) ? '#ef4444' : 'inherit' }}>
                      {p.stock} {p.stock < (p.low_stock_threshold || 5) && '⚠️'}
                    </td>
                    <td style={styles.td}>
                      {p.barcode ? (
                        <button onClick={() => handlePrintBarcode(p)} style={styles.barcodeBtn}>
                          🔳 Print
                        </button>
                      ) : (
                        <span style={{ color: '#888', fontSize: '12px' }}>No barcode</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => handleEditClick(p)} style={styles.editBtn}>Edit</button>
                      <button onClick={() => handleDelete(p.id)} style={styles.deleteBtn}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title: { fontSize: '24px', margin: 0 },
  card: { background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem' },
  form: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' },
  input: { padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', flex: 1, minWidth: '120px' },
  button: { padding: '10px 20px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
  search: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '1rem', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  th: { padding: '12px 16px', background: '#4f46e5', color: '#fff', textAlign: 'left', fontSize: '14px' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '14px' },
  editBtn: { padding: '6px 12px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '6px', cursor: 'pointer', marginRight: '4px', fontSize: '13px' },
  deleteBtn: { padding: '6px 12px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  barcodeBtn: { padding: '6px 12px', background: '#f5f3ff', color: '#7c3aed', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  modalInput: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '0.75rem', boxSizing: 'border-box' },
  saveBtn: { flex: 1, padding: '10px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  cancelBtn: { flex: 1, padding: '10px', background: '#fff', color: '#4f46e5', border: '1px solid #4f46e5', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  codeText: { fontWeight: 'bold', color: '#7c3aed', letterSpacing: '2px', fontSize: '15px' },
  subtitle: { fontSize: '16px', marginBottom: '1rem' },
  loaderWrapper: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem' },
  spinner: { width: '40px', height: '40px', border: '4px solid #e0e7ff', borderTop: '4px solid #4f46e5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loaderText: { marginTop: '1rem', color: '#888', fontSize: '14px' },
  templateBtn: { padding: '8px 14px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  importBtn: { padding: '8px 14px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  importResult: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '1rem', marginTop: '1rem' },
};

export default Products;